import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { isIosShell } from '../lib/nativeBridge';

/**
 * Public pages — reachable without a lab code or sign-in, because app stores
 * and store reviewers must be able to open them cold.
 *
 * Everything stated here is checked against what the app actually does. If the
 * data flow changes, this changes with it.
 */

const UPDATED = '16 September 2026';
const CONTACT = 'noreply.blisx@gmail.com';

function Shell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-ink-950">
      <header className="border-b border-ink-800">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-5 py-4">
          <Link to="/" className="text-lg font-semibold tracking-tight">
            Blisx<span className="text-accent-400">Bench</span>
          </Link>
          <Link
            to="/"
            className="ml-auto inline-flex items-center gap-1 text-sm text-ink-400 hover:text-ink-100"
          >
            <ArrowLeft size={14} />
            Back to the app
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 py-10">
        <h1 className="text-2xl font-semibold">{title}</h1>
        <p className="mt-1 text-sm text-ink-400">Last updated {UPDATED}</p>
        <div className="mt-8 space-y-7 text-[15px] leading-relaxed text-ink-300">{children}</div>

        <footer className="mt-14 border-t border-ink-800 pt-6 text-sm text-ink-400">
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            <Link to="/privacy" className="hover:text-ink-100">
              Privacy
            </Link>
            <Link to="/terms" className="hover:text-ink-100">
              Terms
            </Link>
            <Link to="/support" className="hover:text-ink-100">
              Support
            </Link>
            <a href={`mailto:${CONTACT}`} className="hover:text-ink-100">
              {CONTACT}
            </a>
          </div>
        </footer>
      </main>
    </div>
  );
}

function H({ children }: { children: React.ReactNode }) {
  return <h2 className="mb-2 text-base font-semibold text-ink-100">{children}</h2>;
}

function Privacy() {
  return (
    <Shell title="Privacy Policy">
      <section>
        <p>
          BlisxBench records laboratory measurements. It is used by named researchers who sign in
          with Apple, with Google, or with an email address. This policy describes exactly what the
          app stores, where it goes, and what it never does.
        </p>
      </section>

      <section>
        <H>What we collect</H>
        <ul className="list-disc space-y-2 pl-5">
          <li>
            <strong className="text-ink-100">Account details from however you sign in</strong> —
            your email address and display name, and a profile picture URL if your provider supplies
            one. We never see or receive your Google or Apple password. Sign in with Apple lets you
            hide your real address, in which case we only ever hold the relay address Apple gives
            us. An email-and-password account stores the password only with Firebase
            Authentication, hashed, never in a form we can read.
          </li>
          <li>
            <strong className="text-ink-100">Measurements you enter</strong> — sample names, the
            values you record against them, dates and notes.
          </li>
          <li>
            <strong className="text-ink-100">Photographs you take</strong> of instrument screens,
            and <strong className="text-ink-100">voice transcripts</strong> when you dictate a
            reading. Both are retained so any recorded number can be checked against its source
            later. That audit trail is the point of the app.
          </li>
          <li>
            <strong className="text-ink-100">A lab code</strong>, if you enter one, stored in your
            browser so you are not asked for it on every visit.
          </li>
          <li>
            <strong className="text-ink-100">Payment records</strong>, if you buy credits — what was
            bought, when, the amount, and a reference from our payment provider. Card numbers are
            entered on HitPay&rsquo;s own page and never reach us.
          </li>
        </ul>
        <p className="mt-3">
          We do not collect location, contacts, health data, advertising identifiers, or anything
          about your activity outside this app.
        </p>
      </section>

      <section>
        <H>How your data is used</H>
        <p>
          Solely to provide the app: storing your measurements, showing them back to you,
          exporting them to a spreadsheet, and sharing a project with colleagues you explicitly
          invite. Nothing else.
        </p>
      </section>

      <section>
        <H>Automated transcription</H>
        <p>
          When you capture a reading by photo or voice, that image or transcript is sent to
          Anthropic&rsquo;s Claude API to be transcribed into numbers. Anthropic processes it to
          return that result and does not use API content to train their models.
        </p>
        <p className="mt-2">
          A transcription is never saved on its own. It is shown to you for confirmation, and only
          what you approve is recorded. Manual entry is always available if you would rather not
          use this.
        </p>
      </section>

      <section>
        <H>Where your data is stored</H>
        <p>
          In Google Firebase (Firestore, Storage and Cloud Functions), under Google Cloud&rsquo;s
          security and privacy commitments. Processing runs in the <code>asia-southeast1</code>{' '}
          region, in Singapore.
        </p>
      </section>

      <section>
        <H>Who can see your data</H>
        <p>
          By default, only you. Security rules restrict every project to its members, enforced on
          the server rather than by the app.
        </p>
        <p className="mt-2">
          A project becomes visible to someone else only when its owner explicitly adds them by
          email address. The owner can remove that access at any time, and a member can remove
          themselves.
        </p>
      </section>

      <section>
        <H>What we never do</H>
        <ul className="list-disc space-y-1.5 pl-5">
          <li>We never sell or rent your data.</li>
          <li>We do not show advertising or share data with advertisers.</li>
          <li>There are no third-party analytics or tracking scripts in this app.</li>
          <li>We do not track you across other apps or websites.</li>
          <li>We do not use your research data to train any machine-learning model.</li>
        </ul>
      </section>

      <section>
        <H>Keeping and deleting data</H>
        <p>
          Your data is kept until you delete it. Deleting a project removes everything recorded in
          it and its capture history, including the stored photographs and transcripts.
        </p>
        <p className="mt-2">
          To delete your account and everything in it, email{' '}
          <a className="text-accent-400 hover:underline" href={`mailto:${CONTACT}`}>
            {CONTACT}
          </a>{' '}
          from your account address. We will confirm within 30 days. You can export any project to
          a spreadsheet first — the export button is on every project.
        </p>
      </section>

      <section>
        <H>Your rights</H>
        <p>
          You may request a copy of your data, ask for corrections, or ask for deletion. The export
          feature already gives you a complete copy of any project in a standard spreadsheet, at any
          time, without asking us.
        </p>
      </section>

      <section>
        <H>Children</H>
        <p>
          BlisxBench is a professional laboratory tool and is not directed at children under 13. We
          do not knowingly collect data from them.
        </p>
      </section>

      <section>
        <H>Changes</H>
        <p>
          If this policy changes in a way that affects how your data is handled, the date above will
          change and material changes will be noted in the app.
        </p>
      </section>

      <section>
        <H>Contact</H>
        <p>
          Questions about this policy or your data:{' '}
          <a className="text-accent-400 hover:underline" href={`mailto:${CONTACT}`}>
            {CONTACT}
          </a>
        </p>
      </section>
    </Shell>
  );
}

function Terms() {
  return (
    <Shell title="Terms of Use">
      <section>
        <H>The service</H>
        <p>
          BlisxBench records laboratory measurements and exports them to a spreadsheet. Anyone may
          create an account and use it. Recording measurements by hand, organising them into
          projects and exporting the spreadsheet are free and unlimited.
        </p>
        <p className="mt-2">
          Reading values automatically from a photograph or a spoken phrase is metered, because each
          use calls a paid service. See Credits and payment below.
        </p>
      </section>

      <section>
        <H>Your account</H>
        <p>
          Sign in with Apple, with Google, or with an email address and password. An eight-digit lab
          code is optional: it links your account to a lab that already exists, and the app works
          without one.
        </p>
        <p className="mt-2">
          You are responsible for what happens under your account. One account is one person — do
          not share credentials.
        </p>
      </section>

      <section>
        <H>Your responsibilities</H>
        <ul className="list-disc space-y-1.5 pl-5">
          <li>Keep your lab code within your lab, if you use one.</li>
          <li>Use the app only for data you are entitled to record.</li>
          <li>
            Check every value before confirming it. The app asks you to confirm each reading for a
            reason.
          </li>
        </ul>
      </section>

      <section>
        <H>Automated transcription is an aid, not an authority</H>
        <p>
          The app can read numbers from a photograph or a spoken phrase. This is assistance, not a
          guarantee. Transcription can misread a digit, and a misread digit looks exactly like a
          correct one.
        </p>
        <p className="mt-2">
          Nothing is recorded until you confirm it, the original photograph or transcript is kept
          alongside every value, and you remain responsible for the accuracy of your data. Do not
          rely on a transcribed number you have not checked against its source.
        </p>
      </section>

      <section>
        <H>Credits and payment</H>
        <p>
          Automatic transcription is paid for in credits. A dictated reading costs one credit; a
          photographed or uploaded sheet costs four, as does the trial photo that sets up a new
          reader. Some labs use the
          feature under separately arranged terms and are never charged — if that applies to yours,
          the billing page says so and there is nothing to buy.
        </p>
        <p className="mt-2">
          Credits are bought in packs. Prices are shown in Singapore dollars at checkout, together
          with any tax that applies. Payment is handled by HitPay on their own page: we never
          receive, see or store your card details. A receipt is emailed to the address on your
          account after every payment.
        </p>
        <ul className="mt-3 list-disc space-y-1.5 pl-5">
          <li>
            A read that fails, is declined, or comes back unusable is not charged. You are only
            charged for a result you can use.
          </li>
          <li>Credits do not expire.</li>
          <li>
            Credits are not returnable or exchangeable for money once bought, except where the law
            requires otherwise.
          </li>
          <li>
            If a payment succeeds and the credits do not appear on your account, tell us: that is a
            fault on our side and we will put it right.
          </li>
          <li>
            Credits have no cash value, cannot be transferred between accounts, and cannot be
            exchanged for anything other than use of this app.
          </li>
          <li>
            Prices may change. Credits you have already bought are unaffected by a later price
            change.
          </li>
        </ul>
      </section>

      <section>
        <H>Availability</H>
        <p>
          The app is provided as is, without warranty. It depends on services we do not control, and
          may be unavailable at times. Capture works offline and syncs when you reconnect, but you
          should export anything you cannot afford to lose.
        </p>
      </section>

      <section>
        <H>Liability</H>
        <p>
          To the extent permitted by law, we are not liable for loss arising from use of this app,
          including loss of data or decisions taken on the basis of recorded measurements.
        </p>
      </section>

      <section>
        <H>Ending access</H>
        <p>
          You may stop using the app at any time and request deletion of your data. We may withdraw
          access to an account or a lab code that is misused. Credits held at that point are not
          exchanged for money.
        </p>
      </section>

      <section>
        <H>Contact</H>
        <p>
          <a className="text-accent-400 hover:underline" href={`mailto:${CONTACT}`}>
            {CONTACT}
          </a>
        </p>
      </section>
    </Shell>
  );
}

function Support() {
  return (
    <Shell title="Support">
      <section>
        <p>
          Email{' '}
          <a className="text-accent-400 hover:underline" href={`mailto:${CONTACT}`}>
            {CONTACT}
          </a>
          . Include your lab name and, if something went wrong with a specific reading, roughly when
          it happened — every capture is timestamped, which makes it quick to find.
        </p>
      </section>

      <section>
        <H>Common questions</H>
        <dl className="space-y-4">
          <div>
            <dt className="font-medium text-ink-100">Do I need a lab code?</dt>
            <dd className="mt-1">
              No. Sign in and the app keeps your records under your own account. A code only links
              you to a lab that already exists, so colleagues appear in the sharing list — your lab
              administrator issues it, it is eight digits, and you enter it once per device.
            </dd>
          </div>
          <div>
            <dt className="font-medium text-ink-100">I have run out of credits.</dt>
            <dd className="mt-1">
              Credits cover automatic reading of photographs and dictation. Typing values in and
              exporting the spreadsheet never cost anything and keep working. Open Credits from the
              projects screen to see your balance{isIosShell() ? '.' : ' and top up.'}
            </dd>
          </div>
          <div>
            <dt className="font-medium text-ink-100">A transcribed number is wrong.</dt>
            <dd className="mt-1">
              Correct it on the confirmation screen before saving, or tap any value afterwards to
              edit it. The original photograph stays attached either way.
            </dd>
          </div>
          <div>
            <dt className="font-medium text-ink-100">The voice button is missing.</dt>
            <dd className="mt-1">
              Voice needs browser speech recognition, which some browsers and the packaged app do
              not provide. Photo and manual entry always work.
            </dd>
          </div>
          <div>
            <dt className="font-medium text-ink-100">I am in a basement with no signal.</dt>
            <dd className="mt-1">
              Keep capturing. Photos queue on the device and upload when you reconnect; the header
              shows how many are waiting.
            </dd>
          </div>
          <div>
            <dt className="font-medium text-ink-100">How do I get my data out?</dt>
            <dd className="mt-1">
              Export .xlsx on any project. It includes every measurement plus a Captures sheet
              listing how each number was recorded.
            </dd>
          </div>
        </dl>
      </section>
    </Shell>
  );
}

/** One route component, three pages — they share a shell and a footer. */
export default function Legal() {
  const { page } = useParams();
  if (page === 'terms') return <Terms />;
  if (page === 'support') return <Support />;
  return <Privacy />;
}
